import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ArrowLeft, RefreshCw, Loader2, FileText, Users, Activity, Film, Image, TrendingUp, Info } from 'lucide-react';
import { Sidebar } from '../../components/Sidebar';
import { caApi, errMsg, dateText, BTN_PRIMARY, CARD, BLUE, Avatar, Handle } from './caShared';
import { PostAnalysisTab } from './PostAnalysisTab';
import { CreatorAnalysisTab } from './CreatorAnalysisTab';

const DURATIONS = [[30, 'Last 30 days'], [90, 'Last 90 days'], [180, 'Last 6 months'], [365, 'Last 12 months']];

const Tile = ({ icon: Icon, label, value, testid }) => (
  <div className={`${CARD} p-5 flex items-center gap-4`} data-testid={testid}>
    <div className="h-11 w-11 rounded-lg bg-[#3B4FD8]/10 text-[#3B4FD8] flex items-center justify-center"><Icon size={20} /></div>
    <div><div className="text-xs font-medium text-[#6B7280]">{label}</div><div className="text-2xl font-bold text-[#111827] dark:text-white">{value}</div></div>
  </div>
);

const Timeline = ({ data }) => (
  <div className={`${CARD} p-5`} data-testid="posts-over-time-chart">
    <div className="text-sm font-semibold text-[#111827] dark:text-white mb-4">Posts Over Time <span className="font-normal text-[#9CA3AF]">· weekly</span></div>
    <div className="h-56"><ResponsiveContainer>
      <BarChart data={data} margin={{ left: -20, right: 8 }}>
        <CartesianGrid vertical={false} stroke="#EEF0F6" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: 'rgba(59,79,216,0.06)' }} formatter={(v) => [`${v} posts`, 'Posts']} labelFormatter={(l) => `Week of ${l}`} />
        <Bar dataKey="posts" fill={BLUE} radius={[4, 4, 0, 0]} maxBarSize={28} />
      </BarChart>
    </ResponsiveContainer></div>
  </div>
);

export default function CompetitorReport() {
  const { id } = useParams();
  const [days, setDays] = useState(90);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('posts');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  const load = useCallback(() => caApi.get(`/${id}/report`, { days, page }).then((d) => {
    setData(d); setErr(null);
    clearTimeout(timer.current);
    if (d.competitor.scrape_status === 'running') timer.current = setTimeout(() => load(), 1500);
  }).catch((e) => setErr(errMsg(e, 'Could not load report'))), [id, days, page]);
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  const refresh = async () => {
    try { await caApi.post(`/${id}/scrape`); toast.info('Refreshing data…', { description: 'Apify integration pending — stub data only.' }); setPage(1); load(); } catch (e) { toast.error(errMsg(e, 'Refresh failed')); }
  };

  if (err) return <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8"><div className={`${CARD} p-8 text-sm text-[#6B7280]`} data-testid="competitor-report-error">{err} <Link to="/competitor-analysis" className="text-[#3B4FD8] font-semibold">Back to list</Link></div></div></div>;
  const c = data?.competitor;
  const s = data?.stats;
  const running = c?.scrape_status === 'running';
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-[#F7F8FC] dark:bg-[#0A0B0D] min-w-0" data-testid="competitor-report-page">
        <div className="max-w-[1300px] mx-auto space-y-6">
          <Link to="/competitor-analysis" className="text-sm text-[#6B7280] hover:text-[#3B4FD8] inline-flex items-center gap-1 transition-colors" data-testid="competitor-report-back"><ArrowLeft size={14} /> Competitor Analysis</Link>
          {!data ? <Loader2 className="animate-spin text-[#3B4FD8]" size={20} /> : (
            <>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <Avatar name={c.name} size={56} />
                  <div><h1 className="text-3xl font-bold tracking-tight text-[#111827] dark:text-white" data-testid="competitor-report-name">{c.name}</h1>
                    <div className="flex items-center gap-3 mt-0.5"><Handle h={c.instagram_handle} /><span className="text-xs text-[#9CA3AF]">Updated {dateText(c.last_scraped_at)}</span></div></div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={days} onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }} className="h-10 rounded-full px-4 text-sm font-medium text-[#3B4FD8] bg-[#3B4FD8]/10 border-none focus:outline-none" data-testid="competitor-duration-select">
                    {DURATIONS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
                  </select>
                  <button onClick={refresh} disabled={running} className={BTN_PRIMARY} data-testid="competitor-refresh-btn">{running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {running ? 'Pulling data…' : 'Refresh Data'}</button>
                </div>
              </div>
              {c.scrape_note && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 inline-flex items-center gap-2" data-testid="competitor-stub-note"><Info size={13} /> {c.scrape_note}</div>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="competitor-stat-tiles">
                <Tile icon={FileText} label="Total Posts" value={s.total_posts} testid="stat-total-posts" />
                <Tile icon={Users} label="Total Creators Tagged" value={s.creators_tagged} testid="stat-creators-tagged" />
                <Tile icon={Activity} label="Avg Engagement Rate" value={`${s.avg_er}%`} testid="stat-avg-er" />
                <Tile icon={Film} label="Total Reels" value={s.total_reels} testid="stat-total-reels" />
                <Tile icon={Image} label="Total Static/Carousel" value={s.total_static} testid="stat-total-static" />
                <Tile icon={TrendingUp} label="Reel ER%" value={`${s.reel_er}%`} testid="stat-reel-er" />
              </div>
              <Timeline data={data.timeline} />
              <div className="border-b border-[#E6E8F0] dark:border-white/10 flex gap-6" data-testid="competitor-report-tabs">
                {[['posts', 'Post Analysis'], ['creators', 'Creator Analysis']].map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? 'border-[#3B4FD8] text-[#3B4FD8]' : 'border-transparent text-[#6B7280] hover:text-[#111827]'}`} data-testid={`competitor-tab-${k}`}>{l}{k === 'creators' && ` (${data.creators.length})`}</button>
                ))}
              </div>
              {tab === 'posts' ? <PostAnalysisTab data={data} page={page} setPage={setPage} /> : <CreatorAnalysisTab creators={data.creators} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
